import { useState, useMemo, useEffect } from "react";
import { Card, Button, Input, Empty, Tag, Modal, message, Spin, Table, Badge } from "antd";
import {
  MinusCircleOutlined,
  ArrowRightOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import Title from "antd/lib/typography/Title";
import { Icon } from "Components";
import { confirmMapping, getExternalMerchants } from "Services/Api/PciCompliance/Mapping.Service";
import { TenantMapping } from "../Mapping";
import InfiniteScroll from 'react-infinite-scroll-component';
import { MappingComplete } from "../components/MappingComplete";
const { Search } = Input;

interface VenzaProperty {
  id: string | number;
  propertyName: string;
}

interface EncytroProperty {
  id: string;
  propertyName: string;
}

interface NewMapping {
  id: string;
  venzaProperty: VenzaProperty;
  encytroProperty: EncytroProperty;
}

interface UnmappedPropertiesProps {
  tenantInfo: TenantMapping;
  status: "complete" | "incomplete" | "needs-review" | "not-started";
  initialVenzaProperties: VenzaProperty[],
  onBack: () => void;
}

interface RemovedProperty {
  id: string;
  type: "venza" | "pciPortal";
  property: VenzaProperty | EncytroProperty;
}

export function UnMappingContainer({
  tenantInfo,
  initialVenzaProperties,
  status,
  onBack,
}: UnmappedPropertiesProps) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 576;

  const PAGE_SIZE = 1;
  const PAGE_VENZA_SIZE = 10;

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [visibleVenzaCount, setvisibleVenzaCount] = useState(PAGE_VENZA_SIZE);
  const [venzaProperties, setVenzaProperties] = useState<VenzaProperty[]>(
    initialVenzaProperties
  );
  const [encytroProperties, setEncytroProperties] = useState<EncytroProperty[]>(
    []
  );
  const [removedProperties, setRemovedProperties] = useState<RemovedProperty[]>([]);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [submitting, setSubmitting] = useState(false);
  const [pciPropertiesLoading, setPciPropertiesLoading] = useState(true);
  const [newMappings, setNewMappings] = useState<NewMapping[]>([]);

  const [selectedVenza, setSelectedVenza] = useState<string | null | number>(null);
  const [selectedEncytro, setSelectedEncytro] = useState<string | null>(null);
  const [encytroCount, setEncytroCount] = useState(0);
  const [venzaSearch, setVenzaSearch] = useState("");
  const [encytroSearch, setEncytroSearch] = useState("");
  const [mappingSearch, setMappingSearch] = useState("");
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removedId, setRemovedId] = useState<string | null>(null);
  const [clearMappingModalOpen, setClearMappingModalOpen] = useState(false);
  const filteredEncytroProperties = useMemo(() => {
    if (!encytroSearch) return encytroProperties;
    return encytroProperties;
  }, [encytroProperties, encytroSearch]);

  const filteredNewMappings = useMemo(() => {
    if (!mappingSearch) return newMappings;
    const searchLower = mappingSearch.toLowerCase();
    return newMappings.filter(
      (mapping) =>
        mapping?.venzaProperty?.propertyName.toLowerCase().includes(searchLower) ||
        mapping.encytroProperty.propertyName.toLowerCase().includes(searchLower)
    );
  }, [newMappings, mappingSearch]);

  const mappedCount = newMappings.length;
  const removedCount = removedProperties.length;

  const canCreateMapping = selectedVenza !== null && selectedEncytro !== null;

  const handleCreateMapping = () => {
    if (!selectedVenza || !selectedEncytro) return;

    const venzaProp = venzaProperties.find((p) => p.id === selectedVenza);
    const encytroProp = encytroProperties.find((p) => p.id === selectedEncytro);

    if (!venzaProp || !encytroProp) return;

    const newMapping: NewMapping = {
      id: `mapping-${Date.now()}`,
      venzaProperty: venzaProp,
      encytroProperty: encytroProp,
    };

    setNewMappings((prev) => [...prev, newMapping]);

    setVenzaProperties((prev) => prev.filter((p) => p.id !== selectedVenza));
    setEncytroProperties((prev) =>
      prev.filter((p) => p.id !== selectedEncytro)
    );

    setSelectedVenza(null);
    setSelectedEncytro(null);
    message.success("Mapping created successfully");
  };

  const handleRemoveSingleProperty = () => {
    if (selectedVenza !== null && selectedEncytro === null) {
      const prop = venzaProperties.find((p) => p.id === selectedVenza);
      if (prop) {
        setRemovedProperties((prev) => [
          ...prev,
          { id: `removed-${Date.now()}`, type: "venza", property: prop }
        ]);
        setVenzaProperties((prev) => prev.filter((p) => p.id !== selectedVenza));
      }
      setSelectedVenza(null);
    } else if (selectedEncytro !== null && selectedVenza === null) {
      const prop = encytroProperties.find((p) => p.id === selectedEncytro);
      if (prop) {
        setRemovedProperties((prev) => [
          ...prev,
          { id: `removed-${Date.now()}`, type: "pciPortal", property: prop }
        ]);
        setEncytroProperties((prev) => prev.filter((p) => p.id !== selectedEncytro));
      }
      setSelectedEncytro(null);
    }
  };

  const handleRestoreProperty = (removedId: string) => {
    const removed = removedProperties.find((r) => r.id === removedId);
    if (!removed) return;

    if (removed.type === "venza") {
      setVenzaProperties((prev) => [...prev, removed.property as VenzaProperty]);
    } else {
      setEncytroProperties((prev) => [...prev, removed.property as EncytroProperty]);
    }

    setRemovedProperties((prev) => prev.filter((r) => r.id !== removedId));
  };

  const handleClearAllMappings = () => {
    if (newMappings.length === 0) return;
    setClearMappingModalOpen(true);
  };

  const handleConfirmClearAllMappings = () => {
    const venzaProps = newMappings.map((m) => m.venzaProperty);
    const encytroProps = newMappings.map((m) => m.encytroProperty);
    setVenzaProperties((prev) => [...prev, ...venzaProps]);
    setEncytroProperties((prev) => [...prev, ...encytroProps]);
    setNewMappings([]);
    setClearMappingModalOpen(false);
    message.success("All mappings cleared");
  }

  const handleSave = async () => {
    try {
      setSubmitting(true);
      const result = await confirmMapping(
        tenantInfo,
        [...filteredNewMappings.map((c) => ({
          property_id: String(c?.venzaProperty.id) ?? "",
          property_name: String(c?.venzaProperty?.propertyName) ?? "",
          mid: String(c.encytroProperty.id),
        }))],
      );
      message.success("Mapping changes confirmed successfully!");
      setCurrentStep(2);

    } catch (error: any) {
      console.error("Error:", error);

      if (error.status === 404) {
        const errorMessage =
          error.data?.errors?.[0] ||
          error.data?.message ||
          "Validation failed";
        message.error(errorMessage);
      } else if (error.status === 422) {
        const errorMessage =
          error.data?.errors?.[0] ||
          error.data?.message ||
          "Validation failed";

        message.error(errorMessage);
      } else {
        message.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const handleRemoveMapping = (mappingId: string | null) => {
    setRemovedId(mappingId);
  };

  const confirmHandleRemoveMapping = () => {
    const mapping = newMappings.find((m) => m.id === removedId);
    if (!mapping) return;

    setNewMappings((prev) => prev.filter((m) => m.id !== removedId));

    setVenzaProperties((prev) => [...prev, mapping.venzaProperty]);
    setEncytroProperties((prev) => [...prev, mapping.encytroProperty]);
    setRemovedId(null);
    message.success("Mapping removed");
  }

  const fetchProperties = async (merchant: string) => {
    try {
      if (!!merchant && merchant.trim().length < 3) {
        return;
      }
      setPciPropertiesLoading(true);
      const properties = await getExternalMerchants(
        Number(tenantInfo.id),
        Number(tenantInfo.providerId), merchant, undefined, visibleCount ?? PAGE_SIZE
      );
      const filteredProperties = properties.filter(prop =>
        !filteredNewMappings.some(
          mapping => mapping.encytroProperty?.id === prop.mid
        )
      );
      if (filteredProperties.length > 0) {
        setEncytroCount(Number(filteredProperties[0]?.count ?? 0));
      } else {
        setEncytroCount(0);
      }
      setEncytroProperties(prev => {
        const merged = merchant.trim().length >= 3
          ? visibleCount === PAGE_SIZE ?  filteredProperties.map(el => ({
            id: el.mid,
            propertyName: el.name,
          })) : [
            ...prev,
            ...filteredProperties.map(el => ({
              id: el.mid,
              propertyName: el.name,
            })),
          ]
          : [
            ...prev,
            ...filteredProperties.map(el => ({
              id: el.mid,
              propertyName: el.name,
            })),
          ];

        const uniqueMap = new Map<string, EncytroProperty>();
        merged.forEach(item => {
          uniqueMap.set(item.id, item);
        });
        return Array.from(uniqueMap.values());
      });

    } catch (error) {
      message.error("Failed to load properties for mapping.");
    } finally {
      setPciPropertiesLoading(false);
    }
  };

  const filteredVenzaProperties = useMemo(() => {
    if (!venzaSearch) return venzaProperties;
    const searchLower = venzaSearch.toLowerCase();
    return venzaProperties.filter(
      (prop) =>
        prop.propertyName.toLowerCase().includes(searchLower)
    );
  }, [venzaProperties, venzaSearch]);

  const visibleVenzaProperties = useMemo(() => {
    if (venzaSearch.trim()) {
      return filteredVenzaProperties;
    }
    return filteredVenzaProperties.slice(0, visibleVenzaCount);
  }, [filteredVenzaProperties, visibleVenzaCount, venzaSearch]);


  useEffect(() => {
    setvisibleVenzaCount(PAGE_VENZA_SIZE);
  }, [venzaSearch]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [encytroSearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchProperties(encytroSearch ?? "");
    }, 400);
    return () => {
      clearTimeout(handler);
    };
  }, [encytroSearch, visibleCount]);



  if (currentStep === 2) {
    return (
      <div >
        <div className="d-flex justify-end mbx-16">
          <Button
            type="text"
            className="backToMappingSettings"
            onClick={onBack}
          >
            ← Back to Mapping Settings
          </Button>
        </div>

        <MappingComplete
          tenantInfo={tenantInfo}
          mappingCount={filteredNewMappings.length}
          onBackToHome={onBack}
        />
      </div>
    );
  }


  return (
    <Spin spinning={submitting}>
      <Card style={{ borderRadius: "8px" }}>
        <div className="d-flex justify-end mbx-16">
          <Button
            type="text"
            className="backToMappingSettings"
            onClick={onBack}
          >
            ← Back to Mapping Settings
          </Button>
        </div>
        <div className="mbx-16">
          <Title level={3} className="headerTitle">Unmapped Properties</Title>
          <p className="headerSubtitle">Review and map properties that are currently not mapped for </p>
        </div>

        <div className="grid-button mbx-20">
          <Card className="statusCard pendingCard" bodyStyle={{ padding: 16 }}>
            <div className="cardHeader">
              <ExclamationCircleOutlined className="cardIcon" />
              <p>Pending Review</p>
            </div>
            <p className="cardValue">{filteredVenzaProperties.length}</p>
          </Card>

          <Card className="statusCard newCard" bodyStyle={{ padding: 16 }}>
            <div className="cardHeader">

              <Icon type="matches" />
              <p>Mapped</p>
            </div>
            <p className="cardValue">{mappedCount}</p>
          </Card>

          <Card onClick={() => setRemoveDialogOpen(true)} className="statusCard ignoredCard" hoverable bodyStyle={{ padding: 16 }}>
            <div className="cardHeader">
              <MinusCircleOutlined className="cardIcon" />
              <p>Removed</p>
            </div>
            <p className="cardValue">{removedCount}</p>
          </Card>
        </div>

        <Modal visible={false} className="ignoredMappings" footer={null} closeIcon={<span className="closeIcon">×</span>}>
          <div className="modalHeader">
            <Title level={3} className="modalTitle fsx-18 card-foreground">Removed Changes</Title>
            <p className="modalSubtitle text-muted-foreground mbx-32">These mappings have been removed. You can restore them to review if needed.</p>
          </div>
          <div className="mtx-16 pb-2 pt-2"><div className="text-center text-gray-400 py-8 text-muted-foreground ignore-modal">
            <MinusCircleOutlined />
            <p className="fs-1 text-muted-foreground">No removed changes</p></div></div>
        </Modal>

        <Modal className="clearMappings" title="Clear all mappings?" visible={clearMappingModalOpen} closable={false} footer={null} centered>
          <form className="confirmMappingForm" onSubmit={(e) => { e.preventDefault(); handleConfirmClearAllMappings() }}>
            <p className="confirmMappingDescription" style={{ marginBottom: "0px" }}>Are you sure you want to remove all {removedCount} mappings? This cannot be undone.</p>
            <div className="confirmButtonsWrapper d-flex justify-end mtx-16 gapx-8">
              <Button className="backButton" onClick={() => setClearMappingModalOpen(false)}>Cancel</Button>
              <Button htmlType="submit" className="clearMappingsButton" style={{ backgroundColor: "#d22a40" }} type="primary">Clear All Mappings</Button>
            </div>
          </form>
        </Modal>

        <Modal className="clearMappings" title="Remove this mapping" visible={!!removedId} closable={false} footer={null} centered>
          <form className="confirmMappingForm" onSubmit={(e) => { e.preventDefault(); confirmHandleRemoveMapping() }}>
            <p className="confirmMappingDescription mbx-0">The properties will return to the unmapped lists.</p>
            <div className="confirmButtonsWrapper d-flex justify-end mtx-16 gapx-8">
              <Button className="backButton" onClick={() => setRemovedId(null)}>Cancel</Button>
              <Button htmlType="submit" className="back-button-submit">Remove</Button>
            </div>
          </form>
        </Modal>

        <Modal
          className="removedMappings"
          title="Removed Properties"
          visible={removeDialogOpen}
          onCancel={() => setRemoveDialogOpen(false)}
          footer={[
            <Button key="close" onClick={() => setRemoveDialogOpen(false)}>
              Close
            </Button>,
          ]}
        >
          <div className="d-flex gap-8 flex-column" >
            <h2 className="fw-600 fsx-18">Removed Properties</h2>
            <p className="removed-property-description">
              Properties that have been removed from the mapping process. Click restore to add them back.
            </p>
          </div>
          <div className="no-removed-property"
          >
            {removedProperties.length === 0 ? (
              <div className="no-removed-property-icon-wrapper">
                <MinusCircleOutlined className="no-removed-property-icon" />
                <div>No removed properties</div>
              </div>
            ) : (
              removedProperties.map((removed) => (
                <>
                  <div
                    key={removed.id}
                    className="removed-property-name-wrapper"
                  >
                    <div className="flex-1 min-width-0">
                      <div className="mbx-4">
                        <span className="removed-property-badger">
                          {removed.type === "venza" ? "Venza" : "PCI Portal"}
                        </span>
                      </div>

                      <div
                        className="removed-property-name"
                        title={removed.property.propertyName}
                      >
                        {removed.property.propertyName}
                      </div>
                    </div>

                    <Button
                      size="small"
                      type="default"
                      className="restore-button"
                      onClick={() => handleRestoreProperty(removed.id)}
                    >
                      <Icon type="restoreReverse" />
                      Restore
                    </Button>
                  </div>
                </>
              ))
            )}
          </div>
        </Modal>

        <Modal className="confirmMapping" title="Confirm Mapping Change" closable={false} footer={null} centered>
          <form className="confirmMappingForm">
            <p className="confirmMappingDescription">Changing an existing mapping will affect historical results. Are you sure?</p>

            <div className="confirmButtonsWrapper d-flex justify-end mtx-16 gapx-8">
              <Button className="backButton" >Go Back</Button>
              <Button htmlType="submit" className="confirmButton" type="primary">Save Change</Button>
            </div>
          </form>
        </Modal>

        <div className="unmapped-select-description">
          <div className="d-flex items-center unmap-gap-6 fsx-12">
            <Icon type="unmapExclamatory" />
            <span className="text-muted-foreground">Select one property from each list to create a mapping</span></div>
        </div>

        <div className="d-grid gap-16 mbx-24"
          style={{
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1.5fr",
          }}
        >
          <div className="left-box"
            style={{
              height: isMobile ? "auto" : "500px",
            }}
          >
            <div className="left-box-header"
            >
              <h3
              >
                VENZA Unmapped ({filteredVenzaProperties.length})
              </h3>
            </div>

            <div className="p-12">
              <Search
                placeholder="Search..."
                className="custom-select"
                allowClear
                value={venzaSearch}
                onChange={(e) => setVenzaSearch(e.target.value)}
                prefix={<Icon type="saqSearch" />}
              />
            </div>

            <div
              id="venza-scroll"
              className="Iscroll"
            >
              {filteredVenzaProperties.length === 0 ? (
                <Empty
                  description={
                    venzaSearch
                      ? "No properties match your search"
                      : "All VENZA properties are mapped"
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  className="mtx-40"
                />
              ) : (
                <>
                  <InfiniteScroll
                    dataLength={visibleVenzaProperties.length}
                    next={() => {
                      setTimeout(() => {
                        if (visibleVenzaProperties.length < filteredVenzaProperties.length) {
                          setvisibleVenzaCount(prev => prev + PAGE_VENZA_SIZE);
                        }
                      }, 200);
                    }}
                    hasMore={filteredVenzaProperties.length > visibleVenzaProperties.length}
                    loader={<div className="text-center p-8">Loading...</div>}
                    scrollableTarget="venza-scroll"
                  >
                    <div className="gap-8 d-flex flex-column">
                      {visibleVenzaProperties.map((prop, id) => {
                        const isSelected = selectedVenza === prop.id;
                        return (
                          <div
                            key={prop.id + "" + id}
                            onClick={() => isSelected ? setSelectedVenza(null) : setSelectedVenza(prop.id)}
                            style={{
                              padding: "6px 8px 6px 8px",
                              background: isSelected ? "#E6F4FB" : "#fff",
                              border: isSelected
                                ? "1px solid #2b7fff"
                                : "1px solid #d9d9d9",
                              borderLeft: isSelected ? "4px solid #2b7fff" : "1px solid #d9d9d9",
                              borderRadius: "6px",
                              cursor: "pointer",
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.background = "#f5f5f5";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.background = "#fff";
                              }
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#262626",
                                fontWeight: isSelected ? 500 : 400,
                              }}
                            >
                              {prop.propertyName}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </InfiniteScroll>

                </>)}
            </div>


          </div>

          <div className="left-box"
            style={{
              height: isMobile ? "auto" : "500px",
            }}
          >
            <div className="left-box-header" >
              <h3>
                PCI Portal Unmapped ({encytroCount})
                {/* PCI Portal Unmapped ({newMappings.length > 0 ? encytroCount - newMappings.length : encytroCount}) */}
              </h3>
            </div>

            <div className="p-12">
              <Search
                placeholder="Search..."
                className="custom-select"
                allowClear
                value={encytroSearch}
                onChange={(e) => setEncytroSearch(e.target.value)}
                prefix={<Icon type="saqSearch" />}
              />
            </div>

            <div
              id="encytro-scroll"
              className="Iscroll"
            >
              {filteredEncytroProperties.length === 0 ? (
                <Spin spinning={pciPropertiesLoading}>
                  <Empty
                    description={
                      encytroSearch
                        ? "No properties match your search"
                        : "All Encytro properties are mapped"
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    className="mtx-40"
                  /></Spin>
              ) : (
                <Spin spinning={pciPropertiesLoading}>
                  <InfiniteScroll
                    dataLength={filteredEncytroProperties.length}
                    next={() => setVisibleCount(prev => prev + PAGE_SIZE)}
                    hasMore={encytroCount > filteredEncytroProperties.length}
                    loader={pciPropertiesLoading ? <div className="p-8 text-center">Loading...</div> : <></>}
                    scrollableTarget="encytro-scroll"
                  >
                    <div className="d-flex flex-column gap-8">
                      {filteredEncytroProperties.map((prop) => {
                        const isSelected = selectedEncytro === prop.id;
                        return (
                          <div
                            key={prop.id}
                            onClick={() => isSelected ? setSelectedEncytro(null) : setSelectedEncytro(prop.id)}
                            style={{
                              padding: "6px 8px 6px 8px",
                              background: isSelected ? "#E6F4FB" : "#fff",
                              border: isSelected
                                ? "1px solid ##2b7fff"
                                : "1px solid #d9d9d9",
                              borderLeft: isSelected ? "4px solid #2b7fff" : "1px solid #d9d9d9",
                              borderRadius: "6px",
                              cursor: "pointer",
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.background = "#f5f5f5";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.background = "#fff";
                              }
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#262626",
                                fontWeight: isSelected ? 500 : 400,
                              }}
                            >
                              {prop.propertyName}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </InfiniteScroll>
                </Spin>
              )}
            </div>

          </div>

          <div className="new-mappings"
            style={{
              height: isMobile ? "auto" : "500px",
            }}
          >
            <div
              className="new-mappings-header-wrapper"
            >
              <h3 className="new-mappings-header">
                New Mappings ({newMappings.length})
              </h3>
              {newMappings.length > 0 && (
                <Button
                  type="link"
                  size="small"
                  className="clear-all-button"
                  danger
                  onClick={handleClearAllMappings}
                >
                  Clear All
                </Button>
              )}
            </div>

            {newMappings.length > 0 && (
              <div className="p-12">
                <Search
                  placeholder="Search..."
                  className="custom-select new-mapping-select"
                  allowClear
                  value={mappingSearch}
                  onChange={(e) => setMappingSearch(e.target.value)}
                  prefix={<Icon type="saqSearch" />}
                />
              </div>
            )}

            <div className="Iscroll">
              {filteredNewMappings.length === 0 ? (
                <div className="p032 d-flex justify-center items-center flex-column">
                  <ArrowRightOutlined
                    className="arrow-right-outlined"
                  /><div className="no-mappings-notification">
                    No mappings yet.
                    <br />
                    Select to begin.
                  </div>
                </div>
              ) : (

                <div className="d-flex flex-column gap-12" >
                  {filteredNewMappings.map((mapping) => (
                    <><div
                      key={mapping.id}
                      className="mappingCard"
                    >
                      <div className="venza-property-box-wrapper"
                      >
                        <div className="venza-mapping-box"
                        >
                          VENZA
                        </div>
                        <div className="encytro-mapping-property-box "
                        >
                          {mapping.venzaProperty.propertyName}
                        </div>
                      </div>
                      <div className="connection-arrow">
                        <Icon type="connectionArrow" />
                      </div>
                      <div className="encytro-mapping-box-wrapper" >
                        <div className="encytro-mapping-box "
                        >
                          ENCYTRO
                        </div>
                        <div className="encytro-mapping-property-box"
                        >
                          {mapping.encytroProperty.propertyName}
                        </div>
                      </div>

                      <div className="remove-btn-wrapper">
                        {/* Remove Button */}
                        <Button
                          type="text"
                          className="remove-btn"
                          size="small"
                          icon={<Icon type="remove" />}
                          onClick={() => handleRemoveMapping(mapping.id)}
                        />
                      </div>
                    </div>
                    </>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>

        <div className="d-flex justify-center mbx-24 gap-12">
          <Button
            type="primary"
            size="large"
            icon={<ArrowRightOutlined />}
            onClick={handleCreateMapping}
            disabled={!canCreateMapping}
            className="d-flex justify-center items-center"
            style={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#fff",
              backgroundColor: canCreateMapping ? "#0093D0" : "#818088",
              borderColor: canCreateMapping ? "#0093D0" : "#818088",
              minWidth: "200px",
              borderRadius: "0.5rem",
              gap: "8px",
            }}
          >
            Create Mapping
          </Button>

          <Button
            type="primary"
            size="large"
            icon={<MinusCircleOutlined />}
            onClick={handleRemoveSingleProperty}
            className="d-flex justify-center items-center"
            disabled={!(!!selectedVenza !== !!selectedEncytro)}
            style={{
              backgroundColor: (!!selectedVenza !== !!selectedEncytro) ? "#F7921E" : "#efe6da",
              borderColor: (!!selectedVenza !== !!selectedEncytro) ? "#F7921E" : "#efe6da",
              minWidth: "200px",
              fontSize: "14px",
              fontWeight: 500,
              borderRadius: "0.5rem",
              gap: "8px",
            }}
          >
            Remove Property
          </Button>
        </div>

        <div className="footerButtons">
          <Button className="backButton" onClick={onBack}> ← Back to Mapping Settings</Button>
          <Button disabled={filteredNewMappings.length === 0} className={`continueButton`} type="primary"
            onClick={handleSave}>
            Continue
          </Button>
        </div>
      </Card>
    </Spin>
  );
}
